import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";
import DBModal from "../../../component/global/DBModal";
import { post } from "../../../controlers/global/ConfigRequest";
import {
    LOGIN_ADDRESS,
    REGISTER_ADDRESS,
    SEND_OTP,
} from "../../../helperData/ApiAddress";
import {
    applyTokenResponse,
    getLoginErrorMessage,
} from "../../../helperData/panelRoutes";
import { GlobalStateContext } from "../../../layouts/GlobalStates";
import "./style.scss";

const OTP_LENGTH = 5;
const OTP_TIMEOUT = 120;

const normalizeDigits = (value = "") =>
    value
        .replace(/[۰-۹]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
        .replace(/[٠-٩]/g, (digit) => "٠١٢٣٤٥٦٧٨٩".indexOf(digit));

const normalizePhone = (value) => {
    const phone = normalizeDigits(value).replace(/[\s-]/g, "");
    if (/^09\d{9}$/.test(phone)) {
        return `+98${phone.slice(1)}`;
    }
    if (/^\+989\d{9}$/.test(phone)) {
        return phone;
    }
    return null;
};

const isMissingUser = (error) => {
    const data = error?.response?.data;
    const message = Array.isArray(data?.message)
        ? data.message[0]
        : data?.message;
    return (
        message === "User not found." ||
        (typeof data?.detail === "string" &&
            data.detail.toLowerCase().includes("not found"))
    );
};

const emptyForm = {
    tab: 0,
    step: "phone",
    phone: "",
    firstName: "",
    lastName: "",
    otp: "",
    otpTimer: 0,
    debugCode: "",
    error: "",
    loading: false,
};

export default function FeedbackAuthModal({ show, onHide, onSuccess }) {
    const { t } = useTranslation();
    const { setAuth, setRefreshNewCount } = useContext(GlobalStateContext);
    const [form, setForm] = useState(emptyForm);

    const patch = (next) => setForm((current) => ({ ...current, ...next }));

    useEffect(() => {
        if (!show) {
            setForm(emptyForm);
            return undefined;
        }
        return undefined;
    }, [show]);

    useEffect(() => {
        if (!show || form.otpTimer <= 0) return undefined;
        const timer = window.setTimeout(
            () => patch({ otpTimer: form.otpTimer - 1 }),
            1000
        );
        return () => window.clearTimeout(timer);
    }, [show, form.otpTimer]);

    const requestOtp = () => {
        const phone = normalizePhone(form.phone);
        if (!phone) {
            patch({ error: t("contents.auth.phoneErr") });
            return;
        }
        patch({ loading: true, error: "" });
        post(SEND_OTP, { phone_number: phone })
            .then((response) => {
                patch({
                    step: "verify",
                    otp: "",
                    otpTimer: OTP_TIMEOUT,
                    debugCode: response?.otp_debug_code || "",
                    loading: false,
                });
            })
            .catch((error) => {
                if (isMissingUser(error)) {
                    patch({
                        tab: 1,
                        step: "phone",
                        error: t("contents.auth.notFound"),
                        loading: false,
                    });
                    return;
                }
                patch({
                    error: t("share.otp.send.error"),
                    loading: false,
                });
            });
    };

    const registerAndSendOtp = () => {
        const phone = normalizePhone(form.phone);
        const nextError =
            !form.firstName.trim()
                ? t("Src.Component.Global.AuthModal.Register.setNameErr")
                : !form.lastName.trim()
                ? t("Src.Component.Global.AuthModal.Register.setFamilyErr")
                : !phone
                ? t("contents.auth.phoneErr")
                : "";
        if (nextError) {
            patch({ error: nextError });
            return;
        }
        patch({ loading: true, error: "" });
        post(REGISTER_ADDRESS, {
            first_name: form.firstName.trim(),
            last_name: form.lastName.trim(),
            phone_number: phone,
        })
            .then((response) => {
                patch({
                    step: "verify",
                    otp: "",
                    otpTimer: OTP_TIMEOUT,
                    debugCode: response?.otp_debug_code || "",
                    loading: false,
                });
            })
            .catch((error) => {
                const data = error?.response?.data;
                const alreadyRegistered = Boolean(data?.phone_number);
                patch({
                    tab: alreadyRegistered ? 0 : form.tab,
                    error: alreadyRegistered
                        ? t("contents.auth.alreadyRegistered")
                        : t("contents.auth.registerError"),
                    loading: false,
                });
            });
    };

    const verifyOtp = () => {
        const phone = normalizePhone(form.phone);
        const otp = normalizeDigits(form.otp).replace(/\D/g, "");
        if (!phone || otp.length !== OTP_LENGTH) {
            patch({ error: t("otp.err") });
            return;
        }
        patch({ loading: true, error: "" });
        post(LOGIN_ADDRESS, {
            username: phone,
            password: otp,
            grant_type: "password",
        })
            .then((response) => {
                try {
                    applyTokenResponse(setAuth, response);
                    setRefreshNewCount((current) => !current);
                } catch {
                    patch({
                        error: t("contents.auth.registerError"),
                        loading: false,
                    });
                    return;
                }
                onSuccess?.(response);
            })
            .catch((error) => {
                patch({
                    error: getLoginErrorMessage(error, t("otp.err")),
                    loading: false,
                });
            });
    };

    const submitPhone = () => {
        if (form.tab === 1) {
            registerAndSendOtp();
            return;
        }
        requestOtp();
    };

    return (
        <DBModal
            minWidth={400}
            show={show}
            onHide={onHide}
            className="feedback-auth-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={t("contents.auth.title")}
            />
            <DBModal.Tab
                tabs={[t("LogInWindow.Tab"), t("SignUp.Tab")]}
                selected={form.tab}
                onChange={(tab) =>
                    patch({
                        tab,
                        step: "phone",
                        otp: "",
                        error: "",
                        debugCode: "",
                    })
                }
            />
            <DBModal.Content>
                <p className="feedback-auth-modal__lead">
                    {t("contents.auth.lead")}
                </p>
                {form.step === "phone" ? (
                    <div className="feedback-auth-modal__fields">
                        {form.tab === 1 ? (
                            <>
                                <label htmlFor="contents-auth-first-name">
                                    {t("form.name.title")}
                                </label>
                                <input
                                    id="contents-auth-first-name"
                                    value={form.firstName}
                                    autoComplete="given-name"
                                    onChange={(event) =>
                                        patch({
                                            firstName: event.target.value,
                                            error: "",
                                        })
                                    }
                                />
                                <label htmlFor="contents-auth-last-name">
                                    {t("form.lastname.title")}
                                </label>
                                <input
                                    id="contents-auth-last-name"
                                    value={form.lastName}
                                    autoComplete="family-name"
                                    onChange={(event) =>
                                        patch({
                                            lastName: event.target.value,
                                            error: "",
                                        })
                                    }
                                />
                            </>
                        ) : null}
                        <label htmlFor="contents-auth-phone">
                            {t("form.phone.title")}
                        </label>
                        <input
                            id="contents-auth-phone"
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel"
                            dir="ltr"
                            placeholder="09123456789"
                            value={form.phone}
                            onChange={(event) =>
                                patch({ phone: event.target.value, error: "" })
                            }
                        />
                    </div>
                ) : (
                    <div className="feedback-auth-modal__fields">
                        <p>{t("enter.code")}</p>
                        {form.debugCode ? (
                            <div
                                className="feedback-auth-modal__debug"
                                role="status"
                            >
                                <span>{t("share.otp.debug")}</span>
                                <strong dir="ltr">{form.debugCode}</strong>
                            </div>
                        ) : null}
                        <label htmlFor="contents-auth-otp" className="sr-only">
                            {t("enter.code")}
                        </label>
                        <div className="feedback-auth-modal__otp">
                            <input
                                id="contents-auth-otp"
                                maxLength={OTP_LENGTH}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                dir="ltr"
                                value={form.otp}
                                onChange={(event) =>
                                    patch({
                                        otp: normalizeDigits(
                                            event.target.value
                                        ).replace(/\D/g, ""),
                                        error: "",
                                    })
                                }
                            />
                            <button
                                type="button"
                                disabled={form.otpTimer > 0 || form.loading}
                                onClick={requestOtp}
                            >
                                {form.otpTimer
                                    ? form.otpTimer
                                    : t("send.again")}
                            </button>
                        </div>
                    </div>
                )}
                {form.error ? (
                    <p className="feedback-auth-modal__error" role="alert">
                        {form.error}
                    </p>
                ) : null}
                <button
                    type="button"
                    className="feedback-auth-modal__submit"
                    disabled={form.loading}
                    onClick={form.step === "verify" ? verifyOtp : submitPhone}
                >
                    {form.loading ? (
                        <ClipLoader color="#fff" loading size={22} />
                    ) : form.step === "verify" ? (
                        t("LogIn.Button")
                    ) : (
                        t("feedback.login.Otp.Btn.Label")
                    )}
                </button>
            </DBModal.Content>
        </DBModal>
    );
}
